import React, { useEffect } from "react";
import CloseIcon from "@mui/icons-material/Close";
import AddIcon from "@mui/icons-material/Add";
import DragIcon from "@mui/icons-material/DragIndicator";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import TuneIcon from "@mui/icons-material/Tune";
import RestartIcon from "@mui/icons-material/RestartAlt";
import { SIZE_LABELS } from "./widgetLayout";
import "./widgets.css";

const resolve = (value, ctx) => (typeof value === "function" ? value(ctx) : value);

function WidgetFrame({ L, id, span, index, ctx }) {
  const def = L.registry[id];
  const Body = def.Body;
  const editing = L.edit;
  const name = resolve(def.name, ctx);
  const meta = resolve(def.meta, ctx);
  const aside = resolve(def.aside, ctx);
  const count = resolve(def.count, ctx);
  const link = resolve(def.link, ctx);
  const empty = def.isEmpty ? def.isEmpty(ctx) : false;
  const cls = ["wg", `ws${span}`, editing && "ed", L.over === id && "over", L.dragging === id && "dragging"].filter(Boolean).join(" ");
  return (
    <section className={cls} data-testid={`widget-${id}`} aria-label={name} style={{ animationDelay: `${index * 60}ms` }}
      onDragOver={(e) => { if (editing && L.dragging) { e.preventDefault(); L.enterDrag(id); } }}
      onDrop={(e) => { e.preventDefault(); L.drop(id); }}>
      <div className="wg-ch">
        {editing && <span className="wg-hdl" title="Drag to reorder" draggable
          onDragStart={(e) => { e.dataTransfer.effectAllowed = "move"; e.dataTransfer.setData("text/plain", id); L.startDrag(id); }}
          onDragEnd={L.endDrag}><DragIcon sx={{ fontSize: 16 }} /></span>}
        <h3>{name}</h3>
        {meta && <span className="eb" style={{ opacity: 0.5 }}>{meta}</span>}
        {editing ? (
          <div className="wg-tools sp">
            {def.sizes.length > 1 && <div className="wg-sz" role="group" aria-label="Widget width">
              {def.sizes.map((s) => <button type="button" key={s} className={s === span ? "on" : ""} aria-pressed={s === span} onClick={() => L.resize(id, s)}>{SIZE_LABELS[s]}</button>)}
            </div>}
            <button type="button" className="wg-ib" onClick={() => L.move(index, -1)} aria-label={`Move ${name} earlier`}><ChevronLeftIcon sx={{ fontSize: 16 }} /></button>
            <button type="button" className="wg-ib" onClick={() => L.move(index, 1)} aria-label={`Move ${name} later`}><ChevronRightIcon sx={{ fontSize: 16 }} /></button>
            <button type="button" className="wg-ib x" onClick={() => L.remove(id)} aria-label={`Remove ${name}`}><CloseIcon sx={{ fontSize: 16 }} /></button>
          </div>
        ) : aside ? <div className="sp">{aside}</div>
          : count ? <span className="wg-count sp">{count}</span>
            : link ? <a className="sp" href={link.to} style={{ fontSize: 13 }} onClick={(e) => { e.preventDefault(); ctx.navigate(link.to); }}>{link.label}</a> : null}
      </div>
      <div className={editing ? "wg-body-ed" : "wg-body"}>
        {empty ? <p className="wg-empty">{resolve(def.emptyText, ctx)}</p> : <Body ctx={ctx} />}
      </div>
    </section>
  );
}

/** The grid of widgets for a dashboard (rendered inside a 12-column grid container). */
const usable = (L, id, ctx) => !L.registry[id].available || L.registry[id].available(ctx);

export function WidgetGrid({ L, ctx }) {
  const available = Object.keys(L.registry).filter((id) => !L.has(id) && usable(L, id, ctx)).length;
  return (
    <>
      {L.layout.filter(([id]) => usable(L, id, ctx)).map(([id, span], i) => <WidgetFrame key={id} L={L} id={id} span={span} index={i} ctx={ctx} />)}
      {L.edit && available > 0 && (
        <button type="button" className="wg-addt ws4" onClick={() => L.setLib(true)}><AddIcon /><b>Add widget</b><small>{available} available</small></button>
      )}
      {!L.layout.filter(([id]) => usable(L, id, ctx)).length && !L.edit && (
        <div className="wg wg-dempty"><p>Your dashboard has no widgets.</p>
          <button type="button" className="wg-btn" onClick={() => { L.setEdit(true); L.setLib(true); }}><AddIcon sx={{ fontSize: 16 }} />Add widget</button></div>
      )}
    </>
  );
}

/** Slide-in drawer listing every widget with an Add / Remove button. */
export function WidgetLibrary({ L, roleLabel, ctx }) {
  const groups = {};
  Object.entries(L.registry).filter(([id]) => usable(L, id, ctx)).forEach(([id, def]) => { (groups[def.group || "Widgets"] ||= []).push([id, def]); });
  useEffect(() => {
    if (!L.lib) return undefined;
    const onKey = (e) => { if (e.key === "Escape") L.setLib(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [L.lib, L]);
  return (
    <>
      <div className={`wg-scrim${L.lib ? " open" : ""}`} onClick={() => L.setLib(false)}></div>
      <aside className={`wg-lib${L.lib ? " open" : ""}`} aria-hidden={!L.lib} aria-label="Widget library" data-testid="widget-library">
        <div className="wg-lib-h">
          <div><span className="eb">{roleLabel} widgets</span><h3>Add to dashboard</h3><p>{L.layout.filter(([id]) => usable(L, id, ctx)).length} of {Object.values(groups).reduce((n, g) => n + g.length, 0)} on your dashboard</p></div>
          <button type="button" className="wg-ib" onClick={() => L.setLib(false)} aria-label="Close"><CloseIcon sx={{ fontSize: 16 }} /></button>
        </div>
        <div className="wg-lib-l">{Object.entries(groups).map(([group, items]) => (
          <React.Fragment key={group}>
            <div className="wg-lib-g">{group}</div>
            {items.map(([id, def]) => {
              const on = L.has(id);
              const Icon = def.Icon;
              return (
                <div key={id} className={`wg-li${on ? " on" : ""}`} data-testid={`library-${id}`}>
                  <span className="wg-li-ic">{Icon && <Icon sx={{ fontSize: 18 }} />}</span>
                  <div><b>{def.title || resolve(def.name, {})}</b><p>{def.desc}</p></div>
                  {on ? <button type="button" className="wg-go rm" onClick={() => L.remove(id)}>Remove</button>
                    : <button type="button" className="wg-go" onClick={() => L.add(id)}>Add</button>}
                </div>
              );
            })}
          </React.Fragment>
        ))}</div>
      </aside>
    </>
  );
}

export function CustomizeButtons({ L, dark = false }) {
  const cls = `wg-btn${dark ? " dk" : ""}`;
  return L.edit ? (
    <>
      <button type="button" className={cls} onClick={L.reset}><RestartIcon sx={{ fontSize: 16 }} />Reset layout</button>
      <button type="button" className={`wg-btn ${dark ? "inv" : "pri"}`} onClick={() => L.setEdit(false)}>Done</button>
    </>
  ) : <button type="button" className={cls} onClick={() => L.setEdit(true)}><TuneIcon sx={{ fontSize: 16 }} />Customize</button>;
}

export function EditBar({ L }) {
  if (!L.edit) return null;
  return (
    <div className="wg-edbar" role="status"><span>Drag widgets to reorder, set their width, or remove them. Changes save automatically.</span>
      <button type="button" className="wg-btn" onClick={() => L.setLib(true)}><AddIcon sx={{ fontSize: 16 }} />Add widget</button></div>
  );
}

export function SaveErrorBar({ L }) {
  if (!L.saveError) return null;
  return <div className="wg-edbar err" role="alert"><span>{L.saveError}</span><button type="button" className="wg-btn" onClick={L.retrySave}>Retry</button></div>;
}
